import React, { useState, useRef, useEffect } from 'react';
import { Languages, Search, Check, Globe } from 'lucide-react';
import { LANGUAGES, detectSystemLanguage, getLanguageDisplayName } from '../config/languages';
import { useTranslation } from '../hooks/useTranslation';

interface LanguageSelectorProps {
  currentLanguage: string;
  onLanguageChange: (languageCode: string) => void;
  onClose: () => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
  onClose,
}) => {
  const { t } = useTranslation(currentLanguage);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 过滤语言列表
  const filteredLanguages = LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 初始化时聚焦搜索框
  useEffect(() => {
    searchInputRef.current?.focus();

    // 设置当前语言为高亮
    const currentIndex = filteredLanguages.findIndex(lang => lang.code === currentLanguage);
    if (currentIndex !== -1) {
      setHighlightedIndex(currentIndex);
    }
  }, []);

  // 处理键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev < filteredLanguages.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredLanguages.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredLanguages[highlightedIndex]) {
            handleLanguageSelect(filteredLanguages[highlightedIndex].code);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [highlightedIndex, filteredLanguages, onClose]);

  // 自动滚动高亮项到可见区域
  useEffect(() => {
    if (listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  const handleLanguageSelect = (languageCode: string) => {
    if (languageCode === 'auto') {
      // 自动检测系统语言
      const detectedLanguage = detectSystemLanguage();
      onLanguageChange(detectedLanguage);
    } else {
      onLanguageChange(languageCode);
    }
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setHighlightedIndex(0); // 重置高亮到第一项
  };

  return (
    <div className="fixed inset-0 bg-transparent z-50" onClick={onClose}>
      {/* 语言选择器 - 定位在用户菜单右侧 */}
      <div
        className="absolute left-64 bottom-16 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 animate-slide-in-right max-h-96 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('chooseLanguage')}</h2>
              <p className="text-xs text-gray-600">
                {t('chooseYourPreferredLanguage')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white rounded-lg transition-colors"
              title={t('close')}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <div className="w-3 h-0.5 bg-gray-500 rotate-45 absolute"></div>
                <div className="w-3 h-0.5 bg-gray-500 -rotate-45 absolute"></div>
              </div>
            </button>
          </div>
        </div>

        {/* 搜索框 */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('searchLanguages')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-gray-400 outline-none bg-white shadow-sm transition-colors"
            />
          </div>
        </div>

        {/* 语言列表 */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto max-h-64"
        >
          {filteredLanguages.length === 0 ? (
            <div className="px-6 py-16 text-center text-gray-500">
              <Languages size={64} className="mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">{t('noLanguagesFound')}</p>
            </div>
          ) : (
            filteredLanguages.map((language, index) => (
              <React.Fragment key={language.code}>
                <button
                  onClick={() => handleLanguageSelect(language.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 transition-all duration-200 ${index === highlightedIndex
                      ? 'bg-gray-50'
                      : ''
                    } ${currentLanguage === language.code
                      ? 'bg-gray-100'
                      : ''
                    }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm">
                        {language.code === 'auto' ? t('autoDetect') : language.nativeName}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {language.code === 'auto' ? t('autoDetect') : language.name}
                    </div>
                  </div>

                  {currentLanguage === language.code && (
                    <div className="flex-shrink-0 w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                      <Check className="text-gray-600" size={14} />
                    </div>
                  )}
                </button>

                {/* 在自动检测选项后添加分隔线 */}
                {language.code === 'auto' && (
                  <div className="border-t border-gray-200 mx-3 my-1"></div>
                )}
              </React.Fragment>
            ))
          )}
        </div>

        {/* 底部信息栏 */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-b-lg">
          <div className="flex items-center justify-center">
            <span className="text-xs text-gray-600">
              {getLanguageDisplayName(currentLanguage)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector; 